/**
 * Authentication Store Module
 *
 * Provides JWT-based authentication state management using Zustand.
 * Handles login, logout, token refresh, and session management.
 *
 * @module stores/authStore
 * @security Tokens are stored in localStorage with automatic refresh
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================
// Types & Interfaces
// ============================================

/**
 * User type for authenticated users
 *
 * @property id - Unique user identifier
 * @property email - User's email address
 * @property name - User's display name
 * @property role - User's permission level
 * @property avatar - Optional avatar URL
 * @property organization - User's organization info
 */
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'ANALYST' | 'VIEWER';
  avatar?: string;
  organization?: {
    id: string;
    name: string;
  };
}

/**
 * API response structure for auth endpoints
 */
interface AuthResponse {
  success: boolean;
  data?: {
    user: {
      id: string;
      email: string;
      name: string;
      role: 'ADMIN' | 'ANALYST' | 'VIEWER';
      avatarUrl?: string;
      organization?: {
        id: string;
        name: string;
      };
    };
    accessToken: string;
    refreshToken: string;
    expiresIn: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Auth store state interface
 */
interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpiry: number | null;
  redirectPath: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  loginAsDemo: () => Promise<void>;
  register: (
    email: string,
    password: string,
    name: string,
    organizationName?: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  checkAuth: () => boolean;
  setRedirectPath: (path: string | null) => void;
  getAccessToken: () => string | null;
}

// ============================================
// Constants
// ============================================

/**
 * API Base URL Configuration
 *
 * In production (Vercel): Uses relative URL '/api' since frontend and API
 * are served from the same domain via vercel.json rewrites.
 *
 * In development: Uses absolute URL to localhost:3001 where Express runs.
 *
 * Can be overridden via VITE_API_BASE_URL environment variable.
 */
const API_BASE_URL =
  import.meta.env['VITE_API_BASE_URL'] ??
  (import.meta.env.PROD ? '/api' : 'http://localhost:3001/api');

/**
 * Time before token expiry to trigger refresh (5 minutes)
 */
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Default token expiry (15 minutes in milliseconds)
 * Used when parsing expiry string fails
 */
const DEFAULT_EXPIRY_MS = 15 * 60 * 1000;

// ============================================
// Helper Functions
// ============================================

/**
 * Parse JWT expiry string to milliseconds
 *
 * @param expiresIn - Expiry string (e.g., '15m', '7d', '1h')
 * @returns Milliseconds until expiry
 */
function parseExpiryToMs(expiresIn: string): number {
  const match = expiresIn.match(/^(\d+)([smhd])$/);
  if (!match || !match[1] || !match[2]) return DEFAULT_EXPIRY_MS;

  const value = parseInt(match[1], 10);
  const unit = match[2];

  switch (unit) {
    case 's':
      return value * 1000;
    case 'm':
      return value * 60 * 1000;
    case 'h':
      return value * 60 * 60 * 1000;
    case 'd':
      return value * 24 * 60 * 60 * 1000;
    default:
      return DEFAULT_EXPIRY_MS;
  }
}

/**
 * API user type from response
 */
interface ApiUser {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'ANALYST' | 'VIEWER';
  avatarUrl?: string;
  organization?: {
    id: string;
    name: string;
  };
}

/**
 * Map API user response to User interface
 *
 * @param apiUser - User data from API response
 * @returns Normalized User object
 */
function mapApiUserToUser(apiUser: ApiUser): User {
  return {
    id: apiUser.id,
    email: apiUser.email,
    name: apiUser.name,
    role: apiUser.role,
    avatar: apiUser.avatarUrl,
    organization: apiUser.organization,
  };
}

// ============================================
// Auth Store
// ============================================

/**
 * Authentication store for managing user sessions
 *
 * Features:
 * - Real API authentication with JWT tokens
 * - Automatic token refresh before expiry
 * - Demo user quick login (development only)
 * - Session persistence with localStorage
 * - Redirect path preservation for post-login navigation
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout } = useAuthStore();
 *
 * // Login with credentials
 * await login('user@example.com', 'password');
 *
 * // Quick demo login (development)
 * await loginAsDemo();
 *
 * // Logout
 * await logout();
 *
 * // Get token for API calls
 * const token = getAccessToken();
 * ```
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      sessionExpiry: null,
      redirectPath: null,

      /**
       * Login with email and password
       *
       * @param email - User's email address
       * @param password - User's password
       * @throws Error if login fails
       *
       * @security Credentials are sent via HTTPS to the auth API
       */
      login: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });

          const data: AuthResponse = await response.json();

          if (!response.ok || !data.success || !data.data) {
            throw new Error(data.error?.message ?? 'Login failed');
          }

          const user = mapApiUserToUser(data.data.user);
          const expiryMs = parseExpiryToMs(data.data.expiresIn);
          const sessionExpiry = Date.now() + expiryMs;

          set({
            user,
            accessToken: data.data.accessToken,
            refreshToken: data.data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            sessionExpiry,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      /**
       * Quick login as demo user
       *
       * Uses the demo endpoint which doesn't require a password.
       * Only available in development mode.
       *
       * @throws Error if demo login fails
       */
      loginAsDemo: async () => {
        set({ isLoading: true });

        try {
          const response = await fetch(`${API_BASE_URL}/auth/demo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });

          const data: AuthResponse = await response.json();

          if (!response.ok || !data.success || !data.data) {
            throw new Error(data.error?.message ?? 'Demo login failed');
          }

          const user = mapApiUserToUser(data.data.user);
          const expiryMs = parseExpiryToMs(data.data.expiresIn);
          const sessionExpiry = Date.now() + expiryMs;

          set({
            user,
            accessToken: data.data.accessToken,
            refreshToken: data.data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            sessionExpiry,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      /**
       * Register a new user account
       *
       * @param email - User's email address
       * @param password - User's password (min 8 characters)
       * @param name - User's display name
       * @param organizationName - Optional organization name
       * @throws Error if registration fails
       */
      register: async (
        email: string,
        password: string,
        name: string,
        organizationName?: string
      ) => {
        set({ isLoading: true });

        try {
          const response = await fetch(`${API_BASE_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name, organizationName }),
          });

          const data: AuthResponse = await response.json();

          if (!response.ok || !data.success || !data.data) {
            throw new Error(data.error?.message ?? 'Registration failed');
          }

          const user = mapApiUserToUser(data.data.user);
          const expiryMs = parseExpiryToMs(data.data.expiresIn);
          const sessionExpiry = Date.now() + expiryMs;

          set({
            user,
            accessToken: data.data.accessToken,
            refreshToken: data.data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            sessionExpiry,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      /**
       * Logout and clear session
       *
       * Revokes the refresh token on the server and clears local state.
       */
      logout: async () => {
        const { accessToken, refreshToken } = get();

        // Best effort logout on server
        if (accessToken && refreshToken) {
          try {
            await fetch(`${API_BASE_URL}/auth/logout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ refreshToken }),
            });
          } catch {
            // Ignore errors - we'll clear local state regardless
          }
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          sessionExpiry: null,
          redirectPath: null,
        });
      },

      /**
       * Refresh the access token using the refresh token
       *
       * @returns True if refresh was successful, false otherwise
       *
       * @security Uses refresh token rotation for improved security
       */
      refreshSession: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          return false;
        }

        try {
          const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });

          const data: AuthResponse = await response.json();

          if (!response.ok || !data.success || !data.data) {
            // Refresh failed - clear session
            get().logout();
            return false;
          }

          const user = mapApiUserToUser(data.data.user);
          const expiryMs = parseExpiryToMs(data.data.expiresIn);
          const sessionExpiry = Date.now() + expiryMs;

          set({
            user,
            accessToken: data.data.accessToken,
            refreshToken: data.data.refreshToken,
            isAuthenticated: true,
            sessionExpiry,
          });

          return true;
        } catch {
          // Refresh failed - clear session
          get().logout();
          return false;
        }
      },

      /**
       * Check if current session is valid
       *
       * Returns false and triggers logout if session has expired.
       * Automatically refreshes token if close to expiry.
       *
       * @returns True if session is valid
       */
      checkAuth: () => {
        const { sessionExpiry, isAuthenticated, refreshSession } = get();

        if (!isAuthenticated) {
          return false;
        }

        // Check if session has expired
        if (sessionExpiry && Date.now() > sessionExpiry) {
          get().logout();
          return false;
        }

        // Proactively refresh if close to expiry
        if (sessionExpiry && Date.now() > sessionExpiry - REFRESH_THRESHOLD_MS) {
          // Fire and forget - don't block the check
          refreshSession();
        }

        return true;
      },

      /**
       * Set the path to redirect to after login
       *
       * @param path - Path to redirect to, or null to clear
       */
      setRedirectPath: (path: string | null) => {
        set({ redirectPath: path });
      },

      /**
       * Get the current access token
       *
       * @returns Access token or null if not authenticated
       */
      getAccessToken: () => {
        return get().accessToken;
      },
    }),
    {
      name: 'gi-auth-storage',
      // Only persist specific fields
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        sessionExpiry: state.sessionExpiry,
      }),
    }
  )
);

// ============================================
// Auth Interceptor Setup
// ============================================

/**
 * Get authorization header for API requests
 *
 * Call this function to get the Authorization header value
 * for authenticated API requests.
 *
 * @returns Authorization header value or empty string
 *
 * @example
 * ```typescript
 * const headers = {
 *   'Authorization': getAuthHeader(),
 *   'Content-Type': 'application/json',
 * };
 * ```
 */
export function getAuthHeader(): string {
  const { accessToken } = useAuthStore.getState();
  return accessToken ? `Bearer ${accessToken}` : '';
}

/**
 * Check if user is authenticated
 *
 * Standalone function for use outside React components.
 *
 * @returns True if user is authenticated with valid session
 */
export function isUserAuthenticated(): boolean {
  return useAuthStore.getState().checkAuth();
}
